/**
 * @file ContrastBadge.tsx
 * @description Метка контраста цвета (phase19.16 Р1; uiux-design-r1 §1):
 * «Аа 13,1 ✓» — всегда с цифрой и значком, не только цветом. ✓ от 4,5,
 * ! от 3 до 4,5 («только для крупного текста»), ✕ ниже 3. Подсказка при
 * наведении объясняет, что такое контраст (метка внутри кнопки строки —
 * своей фокусировки у неё нет; на касании то же пояснение строкой в «Цветах»).
 */

import type { SiteContrastLevel } from "@chaiberi/shared";

import { CONTRAST_LEVEL_HINT } from "../../../lib/site-view/color-roles";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import { cn } from "@/lib/utils";

/** Цвет метки по уровню — реестром. */
const LEVEL_CLASS: Readonly<Record<SiteContrastLevel, string>> = {
  ok: "text-success",
  warn: "text-warning",
  fail: "text-destructive",
};

interface Props {
  level: SiteContrastLevel;
  ratioLabel: string;
  mark: string;
  /** Показывать «Аа» — образец текста. */
  sample?: boolean;
  className?: string;
}

/** Что такое контраст — словами владельца (§7.15, сценарий новичка). */
export const CONTRAST_EXPLAINED = "Контраст — насколько текст отличим от фона; норма 4,5 для текста и 3 для кнопок.";

export function ContrastBadge({ level, ratioLabel, mark, sample = true, className }: Props) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={(props) => (
          <span
            {...props}
            className={cn(
              "type-caption inline-flex shrink-0 cursor-help items-center gap-1 font-medium tabular-nums",
              LEVEL_CLASS[level],
              className,
            )}
          >
            {sample ? <span aria-hidden>Аа</span> : null}
            <span>{ratioLabel}</span>
            <span aria-hidden>{mark}</span>
            <span className="sr-only">— {CONTRAST_LEVEL_HINT[level]}</span>
          </span>
        )}
      />
      <TooltipContent className="max-w-72">
        Контраст {ratioLabel} — {CONTRAST_LEVEL_HINT[level]}. {CONTRAST_EXPLAINED}
      </TooltipContent>
    </Tooltip>
  );
}
